/**
 * getDisplayCounterparty — Anzeige-Fallback für die Gegenpartei (BR-18-D,
 * architecture-bank-reconciliation.md §8.12).
 *
 * Kartenbuchungen tragen oft KEINE strukturierte Gegenpartei
 * (`RltdPties/Cdtr`/`Dbtr` fehlt) — der Händler steht nur im Freitext
 * (`RmtInf/Ustrd` bzw. CSV-Verwendungszweck, z.B. "OPENAI USA SAN
 * FRANCISCO"). `extractCounterparty` (`parseCamt053.ts`) liefert dafür
 * `null` → die UI zeigt bisher "-".
 *
 * ANZEIGE-ONLY: dieser Helper schreibt NICHTS in die DB und wird NIRGENDS
 * von Parser oder Matching-Engine aufgerufen. Ein aus Freitext abgeleiteter
 * Händlername ist fuzzy und würde als `counterpartyName` echte
 * `NAME_STRONG`/`NAME_WEAK`-Signale erzeugen → Risiko falscher Auto-/
 * Vorschlags-Matches. `parseCamt053.ts` bleibt daher UNANGETASTET;
 * `counterpartyName` in der DB bleibt `null`.
 *
 * @module bankMatching/getDisplayCounterparty
 */

import { BankTransaction } from '../../types/bankTypes';

/** Max. Länge des angezeigten Segments (danach mit „…" gekappt). */
const MAX_DISPLAY_LENGTH = 40;

/**
 * Trennzeichen, die ein Freitext-Verwendungszweck-Segment beenden: doppeltes
 * Leerzeichen, `//`, `|`, Tab, Zeilenumbruch — typische Trenner in
 * Kartenumsatz-Freitexten ("OPENAI USA SAN FRANCISCO//DE", tabgetrennte
 * Buchungstext-Spalten, mehrzeiliger `Ustrd`-Join).
 */
const SEGMENT_SEPARATOR = /\s{2,}|\/\/|\||\t|\r?\n/;

/**
 * Liefert die anzuzeigende Gegenpartei: `counterpartyName` wenn gesetzt,
 * sonst das erste sinnvolle Segment aus `purpose` (gekappt auf
 * {@link MAX_DISPLAY_LENGTH} Zeichen), sonst `'—'`.
 *
 * @param tx - Minimal-Shape (nur die beiden gelesenen Felder — testbar ohne
 *   volles `BankTransaction`-Fixture).
 * @returns Anzeigetext, NIE leer (`'—'` als Endfallback).
 */
export function getDisplayCounterparty(
  tx: Pick<BankTransaction, 'counterpartyName' | 'purpose'>,
): string {
  const name = tx.counterpartyName?.trim();
  if (name) return name;

  const purpose = tx.purpose?.trim();
  if (!purpose) return '—';

  const segment = purpose.split(SEGMENT_SEPARATOR)[0]?.trim() ?? '';
  if (!segment) return '—';

  if (segment.length <= MAX_DISPLAY_LENGTH) return segment;
  return `${segment.slice(0, MAX_DISPLAY_LENGTH).trimEnd()}…`;
}
